import {DateTime} from "luxon";

export const LOCATION_REGEXP = new RegExp('^[A-Z][0-9]?-(?:[0-9]{1,2}|[A-Z]{1,2})-[0-9]{2}-[0-9]{2}$');
export function formatDate(value, format = 'dd/MM/yyyy') {
    if (!value) {
        return 'Data non valida';
    }

    const dt = DateTime.fromISO(value);
    if (!dt.isValid) {
        return 'Data non valida';
    }

    return dt.toFormat(format);
    // return DateTime.fromSQL(value).toFormat(format);
}

export function normalizeOptionalDateTimeForApi(value, format = 'yyyy-MM-dd HH:mm:ss') {
    if (value === null || value === undefined) {
        return null;
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();

        if (trimmed === '' || trimmed === 'Data non valida') {
            return null;
        }

        const sqlDateTime = DateTime.fromSQL(trimmed);
        if (sqlDateTime.isValid) {
            return sqlDateTime.toFormat(format);
        }

        const isoDateTime = DateTime.fromISO(trimmed);
        if (isoDateTime.isValid) {
            return isoDateTime.toFormat(format);
        }

        return null;
    }

    if (value instanceof DateTime) {
        return value.isValid
            ? value.toFormat(format)
            : null;
    }

    return null;
}

export function formatDateToRelative(value) {
    if (!value) {
        return 'Data non valida';
    }

    const dt = DateTime.fromISO(value);
    if (!dt.isValid) {
        return 'Data non valida';
    }

    return dt.setLocale('it').toRelative();
}

export function nowString(format = 'yyyy-MM-dd HH:mm:ss') {
    return DateTime.now().toFormat(format);
}


export function isItemPicked(item) {
    return !!item.dataOraPik;
}

export function char(basketNumber) {
    return isNaN(basketNumber)
        ? basketNumber
        : String.fromCharCode(basketNumber + 64);
}

export function toLocationCode(code) {
    return code.replaceAll("'", '-').toUpperCase();
}

export function isLocationCode(code) {
    if (!code) {
        return false;
    }
    const locationCode = toLocationCode(code);
    return LOCATION_REGEXP.test(locationCode);
}

export function isItemEan(item, codice) {
    return [item.ean13, item.codice, item.codArticolo, item.codiceAic]
        .filter(v => typeof v === 'string' && v.trim() !== '')
        .includes(codice);
}

function normalizeIdentifier(value) {
    if (value === null || value === undefined) {
        return null;
    }

    const normalized = String(value).trim();
    return normalized === '' ? null : normalized;
}

export function getProductIdentifiers(product) {
    const identifiers = [];
    const seenValues = new Set();
    const candidates = [
        ['ean13', 'EAN', product?.ean13],
        ['codice', 'Codice', product?.codice],
        ['codiceAic', 'Codice AIC', product?.codiceAic],
    ];

    for (const [key, label, rawValue] of candidates) {
        const value = normalizeIdentifier(rawValue);
        if (!value || seenValues.has(value)) {
            continue;
        }

        identifiers.push({ key, label, value });
        seenValues.add(value);
    }

    return identifiers;
}

export function getAdditionalProductIdentifiers(product) {
    return getProductIdentifiers(product).filter(identifier => identifier.key !== 'ean13');
}

export function getLocationCode(location) {
    if (!location.codDeposito || !location.corsia || !location.scaffale || !location.altezza) {
        return 'Ubicazione non valida';
    }
    return `${location.codDeposito}-${location.corsia}-${location.scaffale}-${location.altezza}`
}

export function getMaxStockQuantity(giacenza, stocks, insertingLocationCode) {
    if (giacenza === undefined) {
        return Infinity;
    }
    if (!stocks) {
        return giacenza;
    }
    // Somma le qta degli stock ubicati ALTROVE rispetto a `insertingLocationCode`:
    // lo stock alla stessa ubicazione non occupa spazio residuo perché è quello che
    // stiamo creando o modificando.
    const usedElsewhere = stocks.reduce((sum, stock) => {
        const code = getLocationCode(stock);
        return code === insertingLocationCode ? sum : sum + stock.qtaScorta;
    }, 0);
    return giacenza - usedElsewhere;
}

export function getNextPageParam(lastPage) {
    if (!lastPage) return null;
    return lastPage.pagination?.currentPage < lastPage.pagination?.totalPages
        ? lastPage.pagination.currentPage + 1
        : null
}

export function getPreviousPageParam(lastPage) {
    if (!lastPage) return null;
    return lastPage.pagination?.currentPage > 1
        ? lastPage.pagination.currentPage - 1
        : null
}

export function reducePages(allPages) {
    return allPages?.pages?.length > 0
        ? allPages.pages.reduce((retval, page) => ([...retval, ...(page?.data ?? [])]), [])
        : [];
}

export function replaceOnPages(allPages, item, comparator) {
    return propagateActionToAllPages(allPages, page => {
        return replaceItemInPage(page, item, comparator);
    })
}

export function replaceItemInPage(page, item, comparator) {
    return propagateActionToPageData(page, _ => {
        return page.data.map(e => comparator(e, item) ? {...item} : e);
    });
}

export function findItemPageIndex(allPages, item, comparator) {
    return allPages.pages.findIndex(page => {
        return page.data.findIndex(e => comparator(e, item)) > -1;
    });
}

export function removeFromPages(allPages, item, comparator) {
    return propagateActionToAllPages(allPages, page => {
        return removeItemInPage(page, item, comparator);
    })
}

export function removeItemInPage(page, item, comparator) {
    return propagateActionToPageData(page, p => {
        return p.data.filter(e => false === comparator(e, item));
    });
}

export function propagateActionToPageData(page, action) {
    const data = action(page);
    return {
        ...page,
        data
    }
}

export function propagateActionToAllPages(allPages, action) {
    const pages = allPages.pages.map((page, index) => action(page, index));
    return {
        ...allPages,
        pages
    }
}

export function prependItemOnPages(allPages, item) {
    return propagateActionToAllPages(allPages, (page, index) => {
        const data = index === 0
            ? [item, ...page.data]
            : page.data;
        return {
            ...page,
            data
        }
    })
}

export const dateToStorage = (dt, DATE_FORMAT = 'yyyy-MM-dd') => {
    if (dt instanceof DateTime) {
        return dt.toFormat(DATE_FORMAT);
    }
    throw new Error('La data passata deve essere un\'istanza del DateTime di luxon')
}

export const dateFromStorage = (strDate, DATE_FORMAT = 'yyyy-MM-dd') => {
    return strDate
        ? DateTime.fromFormat(strDate, DATE_FORMAT)
        : null;
}

export const encodeDepotCode =  (code) => {
    return code.replaceAll('/', '__');
}

export const getAppVersion = () => {
    const version = `${__APP_VERSION__}`;

    if (import.meta.env.DEV) {
        return version + '-dev';
    }

    return version;
}

export const isChangingDepot = (sourceDepot, destinationDepot) => {
    const source = sourceDepot.split('-')[0];
    const destination = destinationDepot.split('-')[0];

    if (!source.length) {
        return false;
    }

    return source !== destination;
}

export const getLocationObject = location => {
    const [codDeposito, corsia, scaffale, altezza] = location.split('-');
    return { codDeposito, corsia, scaffale, altezza };
}

export const generateShortname = (fullName) => {
    // Split su spazi E punti seguiti da lettere
    const words = fullName.trim().split(/[\s.]+/).filter(word => word.length > 0);

    if (words.length === 0) return '';
    if (words.length === 1) {
        const clean = words[0].substring(0, 3);
        return clean.charAt(0).toUpperCase() + clean.slice(1).toLowerCase();
    }

    // Prima lettera della prima parola
    const firstLetter = words[0].charAt(0).toUpperCase();

    // Prime 2 lettere dell'ultima parola
    const lastName = words[words.length - 1];
    const lastTwoLetters = lastName.substring(0, 2);

    return firstLetter + lastTwoLetters.charAt(0).toUpperCase() + lastTwoLetters.charAt(1).toLowerCase();
};

export function formatUnixTimestamp(value, format = 'dd/MM/yyyy HH:mm') {
    if (value === null || value === undefined) {
        return 'Data non valida';
    }

    const dt = DateTime.fromSeconds(Number(value));
    if (!dt.isValid) {
        return 'Data non valida';
    }

    return dt.toFormat(format);
}

export function getTimeAgoFromUnixTimestamp(timestampSeconds) {
    if (timestampSeconds === null || timestampSeconds === undefined) {
        return 'Data non valida';
    }

    const ts = Number(timestampSeconds);
    if (Number.isNaN(ts)) {
        return 'Data non valida';
    }

    const minutes = Math.floor((Date.now() / 1000 - ts) / 60);
    if (minutes < 1) return 'Adesso';
    if (minutes < 60) return `${minutes}m fa`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h fa`;

    return `${Math.floor(hours / 24)}g fa`;
}

export function getTimeAgoFromIsoTimestamp(isoTimestamp) {
    if (!isoTimestamp) {
        return 'Data non disponibile';
    }

    const dt = DateTime.fromISO(isoTimestamp);
    if (!dt.isValid) {
        return 'Data non disponibile';
    }

    const minutes = Math.floor(DateTime.now().diff(dt, 'minutes').minutes);
    if (minutes < 1) return 'Adesso';
    if (minutes < 60) return `${minutes}m fa`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h fa`;

    return `${Math.floor(hours / 24)}g fa`;
}

export function formatIsoTimestamp(isoTimestamp, format = 'dd/MM/yyyy HH:mm') {
    if (!isoTimestamp) {
        return 'Data non disponibile';
    }

    const dt = DateTime.fromISO(isoTimestamp);
    if (!dt.isValid) {
        return 'Data non disponibile';
    }

    return dt.toFormat(format);
}
